/* Telegram WebApp helpers — uses the global telegram-web-app.js script. */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: any;
  themeParams: Record<string, string>;
  colorScheme: 'light' | 'dark';
  ready: () => void;
  expand: () => void;
  close: () => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (t: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    showProgress: () => void;
    hideProgress: () => void;
  };
  showConfirm: (msg: string, cb: (ok: boolean) => void) => void;
  showAlert: (msg: string, cb?: () => void) => void;
  setHeaderColor?: (c: string) => void;
  setBackgroundColor?: (c: string) => void;
  onEvent: (ev: string, cb: () => void) => void;
  offEvent: (ev: string, cb: () => void) => void;
  version?: string;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

export const isTelegram = () => !!tg() && !!tg()?.initData;

export const initTelegram = () => {
  const w = tg();
  if (!w) {
    document.documentElement.classList.add(
      window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    );
    return;
  }
  w.ready();
  w.expand();
  applyTheme();
  w.onEvent('themeChanged', applyTheme);
};

const applyTheme = () => {
  const w = tg();
  if (!w) return;
  if (w.colorScheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  const tp = w.themeParams ?? {};
  if (tp.bg_color) document.documentElement.style.setProperty('--tg-bg', tp.bg_color);
  if (tp.text_color) document.documentElement.style.setProperty('--tg-text', tp.text_color);
};

export const haptic = (style: 'light' | 'medium' | 'heavy' = 'light') => {
  tg()?.HapticFeedback?.impactOccurred(style);
};
export const hapticNotify = (type: 'success' | 'error' | 'warning') => {
  tg()?.HapticFeedback?.notificationOccurred(type);
};

export const tgConfirm = (msg: string): Promise<boolean> =>
  new Promise((resolve) => {
    const w = tg();
    if (!w) return resolve(window.confirm(msg));
    w.showConfirm(msg, (ok) => resolve(ok));
  });
